import * as Sentry from '@sentry/nextjs';
import { getSession } from 'next-auth/react';
import {
  BridgeTokenRequestError,
  getBridgeToken,
  refreshUserBridgeToken,
} from './bridgeToken';
import {
  bumpBridgeLogoutEpoch,
  deleteUserBridgeTokens,
  isValidToken,
  readBridgeAccessToken,
  readBridgeLogoutEpoch,
  readBridgeRefreshToken,
  readBridgeTokenSubject,
  storeBridgeTokenSubject,
} from './bridgeTokenCookies';

export type EnsureBridgeTokenParams = {
  /** USG origin without `/api`, e.g. `https://staging.services.unity.com`. */
  usgBaseUrl: string;
  /** Unity ID JWT (`unity_token`) from the session; none means signed out. */
  unityToken?: string;
  /** Genesis user the token is for; a stored token bound to anyone else is dropped. */
  userId?: string;
  /** Login identity retained across bearer refreshes. */
  sessionId?: string;
};

// Heterogeneous by design: a provision resolves a token, a renewal resolves
// the selected token.
const inFlight = new Map<string, Promise<unknown>>();

/**
 * Bumped whenever the session ends. A mint that was already in the air when
 * that happened must not write the old session's token back into the cookies.
 * The cookies are shared by every tab, so the marker has to be too: a counter
 * in this module would only invalidate work started in the tab that signed out.
 */
let generation = 0;

/**
 * Provisioning writes the shared cookie jar, and bridgeToken.ts commits from
 * inside the request, so two overlapping attempts cannot be untangled after
 * the fact: a stale one would overwrite the live token and then delete it on
 * its way out. Attempts therefore queue, and each reads the sign-out marker
 * only once the previous one has finished cleaning up after itself.
 */
let commitQueue: Promise<unknown> = Promise.resolve();

const COMMIT_LOCK = 'compass-bridge-token-commit';

/**
 * `commitQueue` orders this tab, but the cookies and the logout epoch are
 * shared browser-wide, so ordering one tab is not enough: a mint still in the
 * air in a backgrounded tab could land on top of a newer sign-in and then
 * delete it on its epoch check. Web Locks is the one same-origin primitive
 * that spans tabs; where it is missing the in-tab queue is all there is.
 */
const withCommitLock = async <T>(run: () => Promise<T>): Promise<T> =>
  typeof navigator !== 'undefined' && navigator.locks?.request
    ? navigator.locks.request(COMMIT_LOCK, run)
    : run();

/** Runs `run` once every earlier commit, in this tab and any other, is done. */
const enqueueCommit = <T>(run: () => Promise<T>): Promise<T> => {
  const turn = commitQueue.then(() => withCommitLock(run));
  commitQueue = turn.catch(() => undefined);
  return turn;
};

/**
 * `fetchData` has no timeout of its own, and this runs inside the commit lock:
 * a hung session call would stall every provision and the sign-out teardown.
 */
const SESSION_CHECK_TIMEOUT_MS = 15_000;

// Request headers can reuse a recent proof; minting and renewal always check
// again. Bound reuse because server-side session expiry need not bump the epoch.
const SESSION_PROOF_MAX_AGE_MS = 30_000;
let sessionProof:
  | {
      userId: string | undefined;
      sessionId: string | undefined;
      bearer: string | undefined;
      epoch: string;
      generation: number;
      expiresAt: number;
      unityToken: string;
    }
  | undefined;

/**
 * The sign-out authority for user cookies. The logout epoch only fences work
 * that was already in the air: a tab whose session has not synced yet can
 * start a new mint after the bump, capture the bumped value and pass every
 * epoch check. The NextAuth session cookie is the one marker such a tab cannot
 * adopt — signOut clears it on the server, and the server answers from it,
 * not from whatever this tab last rendered.
 */
const liveSessionTokenFor = async (
  userId: string | undefined,
  sessionId: string | undefined,
  reuseProof = false
) => {
  const epoch = readBridgeLogoutEpoch();
  const startedAt = generation;
  const bearer = activeSubject.unityToken;
  if (
    reuseProof &&
    sessionProof &&
    sessionProof.userId === userId &&
    sessionProof.sessionId === sessionId &&
    sessionProof.bearer === bearer &&
    sessionProof.epoch === epoch &&
    sessionProof.generation === startedAt &&
    sessionProof.expiresAt > Date.now()
  ) {
    return sessionProof.unityToken;
  }
  sessionProof = undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), SESSION_CHECK_TIMEOUT_MS);
  });
  try {
    // No broadcast: every other tab would refetch its session on each mint.
    const session = await Promise.race([
      getSession({ broadcast: false }),
      timedOut,
    ]);
    // Before the first refresh, the opaque Genesis token identifies the login.
    // Middleware retains that identity when rotating the bearer.
    const token = session?.accessToken;
    const unityToken =
      token?.genesis_user === userId &&
      isValidToken(sessionId) &&
      (token?.bridge_session_id ?? token?.genesis_access_token) === sessionId &&
      isValidToken(token?.unity_token)
        ? token.unity_token
        : undefined;
    if (
      unityToken &&
      epoch === readBridgeLogoutEpoch() &&
      startedAt === generation &&
      bearer === activeSubject.unityToken
    ) {
      sessionProof = {
        userId,
        sessionId,
        bearer,
        epoch,
        generation: startedAt,
        expiresAt: Date.now() + SESSION_PROOF_MAX_AGE_MS,
        unityToken,
      };
    }
    return unityToken;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * The subject the request path may use a stored user token for. A module-level
 * interceptor cannot call `useSession`, and the live Genesis user sits in the
 * httpOnly NextAuth JWT, so the session hook has to publish it here.
 *
 * `known` is separate from the value on purpose: before the session resolves
 * we cannot tell a token bound to someone else from a perfectly good one, and
 * deleting on that guess would wipe a valid credential on every page load.
 */
let activeSubject: {
  sessionId?: string;
  known: boolean;
  userId: string | undefined;
  unityToken: string | undefined;
} = {
  known: false,
  userId: undefined,
  unityToken: undefined,
};

export const publishActiveBridgeTokenSubject = ({
  userId,
  unityToken,
  sessionId,
}: {
  userId: string | undefined;
  sessionId?: string;
  /** The gateway wants this on bridge routes too, not just on the mint. */
  unityToken: string | undefined;
}) => {
  // Module state on the server is shared by every concurrent request, so an
  // SSR render would leak one visitor's bearer into another's. This only ever
  // serves the browser interceptor; guarded here rather than at the call site
  // so no future caller can reintroduce the leak.
  if (typeof window === 'undefined') {
    return;
  }
  const nextSubject = {
    known: true,
    sessionId,
    userId: isValidToken(userId) ? userId : undefined,
    unityToken: isValidToken(unityToken) ? unityToken : undefined,
  };
  if (
    nextSubject.sessionId !== activeSubject.sessionId ||
    nextSubject.userId !== activeSubject.userId ||
    nextSubject.unityToken !== activeSubject.unityToken
  ) {
    sessionProof = undefined;
  }
  if (
    nextSubject.sessionId !== activeSubject.sessionId ||
    nextSubject.userId !== activeSubject.userId
  ) {
    rejectedRenewal = undefined;
  }
  activeSubject = nextSubject;
};

/** Test seam: back to a fresh tab with no resolved session or renewal failure. */
export const resetActiveBridgeTokenSubject = () => {
  sessionProof = undefined;
  activeSubject = { known: false, userId: undefined, unityToken: undefined };
  rejectedRenewal = undefined;
};

/**
 * Identity gate for request paths that no mount gate ran for — a React Query
 * refetch on window focus, say. `ensureBridgeToken` drops another session's
 * token before it provisions, but the interceptor reads the cookies directly,
 * so without this a page whose session has already flipped to another user
 * would send the previous user's bearer and read their cart.
 *
 * Deletes only on a proven mismatch. An unresolved session is not evidence of
 * one, and deleting on that guess would wipe a valid credential on every load.
 */
export const discardUserBridgeTokensFromAnotherSession = () => {
  if (!activeSubject.known) {
    return;
  }
  const hasUserToken =
    isValidToken(readBridgeAccessToken()) ||
    isValidToken(readBridgeRefreshToken());
  if (!hasUserToken) {
    return;
  }
  const subject = readBridgeTokenSubject();
  if (
    activeSubject.userId === undefined ||
    !isValidToken(subject) ||
    subject !== activeSubject.userId
  ) {
    deleteUserBridgeTokens();
  }
};

/**
 * Whether a stored user token is proven to belong to the live session. Unknown
 * fails closed: not deleting an unverified token is right, but sending one is
 * not, so the request path withholds it until the session says whose it is.
 */
const canUseUserToken = (): boolean =>
  activeSubject.known &&
  activeSubject.userId !== undefined &&
  readBridgeTokenSubject() === activeSubject.userId;

type SelectedBridgeToken = {
  token: string;
  userId: string | undefined;
  sessionId: string | undefined;
};

/** The stored user token this session is allowed to send. */
const usableStoredBridgeToken = (): SelectedBridgeToken | undefined => {
  if (!canUseUserToken()) {
    return undefined;
  }
  const token = readBridgeAccessToken();
  return isValidToken(token)
    ? {
        token,
        userId: activeSubject.userId,
        sessionId: activeSubject.sessionId,
      }
    : undefined;
};

export type BridgeRequestAuthorization = {
  /** `X-Authorization`: the bridge token the commerce endpoints read. */
  xAuthorization: string;
  /** `Authorization`: the gateway rejects bridge routes without it. */
  authorization: string;
};

/**
 * A credential this session may use existed, but no live token came out of
 * it: the refresh failed, timed out or was overtaken by a sign-out. Sending
 * the request anyway would only reach the gateway unauthenticated, so the
 * interceptor rejects it with this instead.
 */
export class BridgeTokenRenewalError extends Error {
  constructor() {
    super(
      'Bridge token: could not renew the user token, so the request was not sent.'
    );
    this.name = 'BridgeTokenRenewalError';
  }
}

// Deleting a rejected refresh cookie must not turn the next request from
// this mounted client into a first-visit guest. Keep failing until a usable
// credential is provisioned or observed (including one written by another tab),
// or the active identity changes or signs out.
let rejectedRenewal: BridgeTokenRenewalError | undefined;

/**
 * Why `undefined` alone could not carry this: "nothing to renew" — a signed-out
 * visitor, whose request goes out with no header by design — and "renewal
 * failed" used to be the same value, so a failure was sent too.
 */
export type BridgeTokenRenewal =
  | ({ status: 'usable' } & SelectedBridgeToken)
  | { status: 'none' }
  | { status: 'failed'; error: BridgeTokenRenewalError };

export type BridgeRequestAuthorizationResult =
  | { status: 'authorized'; headers: BridgeRequestAuthorization }
  | { status: 'none' }
  | { status: 'failed'; error: BridgeTokenRenewalError };

/**
 * Header values for a bridge request, for the interceptor. Withholds a user
 * token this session cannot prove is its own, so an unverified bearer never
 * leaves the browser.
 *
 * The two travel together: the gateway authenticates the caller from
 * `Authorization` — the same `unity_token` the proxy this replaced sent — and
 * only then reads the bridge token, so a user bridge token paired with no
 * `Authorization` is the 401 this exists to avoid.
 */
export const bridgeAuthorizationForRequest = async (
  usgBaseUrl: string
): Promise<BridgeRequestAuthorizationResult> => {
  const selected = await renewBridgeTokenForRequest(usgBaseUrl);
  if (selected.status !== 'usable') {
    return selected;
  }
  const startedAt = generation;
  const epoch = readBridgeLogoutEpoch();
  const unityToken = await liveSessionTokenFor(
    selected.userId,
    selected.sessionId,
    true
  );
  if (
    !unityToken ||
    startedAt !== generation ||
    epoch !== readBridgeLogoutEpoch() ||
    selected.userId !== activeSubject.userId ||
    selected.sessionId !== activeSubject.sessionId
  ) {
    return { status: 'failed', error: new BridgeTokenRenewalError() };
  }
  return {
    status: 'authorized',
    headers: {
      xAuthorization: `Bearer ${selected.token}`,
      authorization: `Bearer ${unityToken}`,
    },
  };
};

/**
 * Bridge cookies outlive the session that minted them: the auth refresh
 * middleware drops the NextAuth cookie on a failed Genesis refresh without
 * going through our signOut teardown. Reusing what is left would hand the next
 * sign-in the previous user's credential, so anything not bound to the current
 * Genesis user is discarded before it can be read.
 */
const dropTokensFromAnotherSession = (userId: string | undefined) => {
  if (!isValidToken(userId) || readBridgeTokenSubject() !== userId) {
    deleteUserBridgeTokens();
  }
};

// commerce-bridge's auth_controller.go answers a missing or unknown refresh token with a 400 naming it.
const REJECTED_REFRESH_TOKEN = /refresh[\s_-]?token/i;

/** Only a rejected credential is spent: another 4xx may be the gateway's (unity-services-gateway #12563). */
const isSpentCredential = (error: unknown) =>
  error instanceof BridgeTokenRequestError &&
  (error.status === 401 ||
    (error.status === 400 && REJECTED_REFRESH_TOKEN.test(error.detail ?? '')));

const provision = async ({
  usgBaseUrl,
  unityToken,
}: {
  usgBaseUrl: string;
  unityToken: string;
}): Promise<string> => {
  if (isValidToken(readBridgeRefreshToken())) {
    try {
      const { access_token } = await refreshUserBridgeToken({ usgBaseUrl });
      return access_token;
    } catch {
      // A user mint keys to the same identity, so falling through is harmless.
    }
  }

  const { access_token } = await getBridgeToken({ usgBaseUrl, unityToken });
  return access_token;
};

/**
 * Whether a bridge token that this session may actually use is in the cookies
 * right now. Gates must read this rather than remember an earlier outcome: the
 * access-token cookie expires on its own, so a remembered "ready" outlives it.
 */
export const hasUsableBridgeToken = (userId: string | undefined): boolean =>
  isValidToken(readBridgeAccessToken()) &&
  isValidToken(userId) &&
  readBridgeTokenSubject() === userId;

type FenceOptions = {
  /** Genesis user the `user` cookies will be bound to. */
  userId?: string;
  sessionId?: string;
  onCommitted?: () => void;
};

/**
 * Runs provisioning work behind the sign-out fence and the commit queue.
 *
 * Resolves to `undefined` instead of rejecting: callers must be able to hold
 * the request back rather than fire an unauthenticated bridge call.
 */
const runFenced = <T>(
  key: string,
  work: (liveUnityToken: string) => Promise<T>,
  { userId, sessionId, onCommitted }: FenceOptions
): Promise<T | undefined> => {
  const pending = inFlight.get(key);
  if (pending) {
    return pending as Promise<T | undefined>;
  }

  // Captured now, not after the queue: a sign-out between this call and this
  // attempt's turn is exactly what the fence has to notice.
  const startedAt = generation;
  const startedAtEpoch = readBridgeLogoutEpoch();

  const isStale = () =>
    startedAt !== generation || startedAtEpoch !== readBridgeLogoutEpoch();

  const attempt: Promise<T | undefined> = enqueueCommit(async () => {
    // Already overtaken while queued or waiting for the lock: return without
    // running the work at all, because `work()` writes the shared cookies as
    // a side effect and would clobber whatever holds them now.
    if (isStale()) {
      return undefined;
    }
    const liveUnityToken = await liveSessionTokenFor(userId, sessionId);
    // A response produced before sign-out may arrive after its epoch bump.
    // Do not start cookie-writing work based on that stale response.
    if (isStale() || !liveUnityToken) {
      return undefined;
    }
    const token = await work(liveUnityToken);
    // bridgeToken.ts writes the cookies itself, so a session that ended
    // mid-flight — here or in another tab — is cleaned up after the fact.
    // The lock is what makes that safe: no other tab can have committed
    // since the check above, so this deletes only what this attempt wrote.
    if (isStale()) {
      deleteUserBridgeTokens();
      return undefined;
    }
    rejectedRenewal = undefined;
    onCommitted?.();
    return token;
  })
    .catch((error) => {
      // BridgeTokenRequestError already drops the bearer headers axios would
      // otherwise carry into this payload.
      Sentry.captureException(error);
      return undefined;
    })
    .finally(() => {
      // A reset can have replaced this entry while it was settling; removing
      // the newer one would send the next caller off to provision again.
      if (inFlight.get(key) === attempt) {
        inFlight.delete(key);
      }
    });

  inFlight.set(key, attempt);
  return attempt;
};

/**
 * Resolves the user bridge token the `X-Authorization` interceptor reads,
 * reusing a live one bound to this session and otherwise refreshing or
 * minting. A signed-out visitor has none: their cart calls go through the
 * guest cart route instead.
 */
export const ensureBridgeToken = (
  params: EnsureBridgeTokenParams
): Promise<string | undefined> => {
  const { usgBaseUrl, unityToken, userId, sessionId } = params;

  dropTokensFromAnotherSession(userId);
  if (!isValidToken(unityToken)) {
    return Promise.resolve(undefined);
  }

  const existing = readBridgeAccessToken();
  if (isValidToken(existing)) {
    rejectedRenewal = undefined;
    return Promise.resolve(existing);
  }

  // Callers that would provision different tokens must not share a result, so
  // only genuinely equivalent work is deduplicated.
  return runFenced(
    JSON.stringify([usgBaseUrl, userId ?? '', unityToken, sessionId ?? '']),
    (liveUnityToken) => provision({ usgBaseUrl, unityToken: liveUnityToken }),
    {
      userId,
      sessionId,
      onCommitted: () => {
        if (isValidToken(userId)) {
          storeBridgeTokenSubject(userId);
        }
      },
    }
  );
};

/**
 * Last line for bridge requests no gate triggered — a React Query refetch on
 * window focus, say, under a page left mounted past the access token's
 * lifetime. Only the refresh cookie is involved, which outlives the access
 * token by days and needs no session context, so this is safe to call from
 * the request interceptor. `none` means there was nothing to renew from;
 * `failed` means there was, and it produced no token.
 */
export const renewBridgeTokenForRequest = async (
  usgBaseUrl: string
): Promise<BridgeTokenRenewal> => {
  discardUserBridgeTokensFromAnotherSession();
  const current = usableStoredBridgeToken();
  if (current) {
    rejectedRenewal = undefined;
    return { status: 'usable', ...current };
  }

  // Renewing the user credential is itself a use of it, so it waits on the
  // same proof.
  if (!canUseUserToken() || !isValidToken(readBridgeRefreshToken())) {
    return rejectedRenewal
      ? { status: 'failed', error: rejectedRenewal }
      : { status: 'none' };
  }

  const { userId, unityToken, sessionId } = activeSubject;
  const renewed = await runFenced(
    JSON.stringify(['renew', usgBaseUrl, userId, unityToken, sessionId]),
    async (): Promise<SelectedBridgeToken | undefined> => {
      try {
        const { access_token } = await refreshUserBridgeToken({ usgBaseUrl });
        // Session expiry and a later sign-in need not bump the logout epoch.
        // Still under the commit lock, discard the old login's cookie write.
        if (
          userId !== activeSubject.userId ||
          sessionId !== activeSubject.sessionId
        ) {
          deleteUserBridgeTokens();
          return undefined;
        }
        return { token: access_token, userId, sessionId };
      } catch (error) {
        // A refresh token the service rejected stays rejected, and the cookie
        // outlives the access token by days: leaving it would re-run this
        // doomed request, and report it, on every later bridge request.
        if (isSpentCredential(error)) {
          // A pending refresh must not poison the identity that replaced it.
          if (
            userId === activeSubject.userId &&
            sessionId === activeSubject.sessionId
          ) {
            rejectedRenewal = new BridgeTokenRenewalError();
          }
          deleteUserBridgeTokens();
        }
        throw error;
      }
    },
    { userId, sessionId }
  );
  return renewed
    ? { status: 'usable', ...renewed }
    : { status: 'failed', error: new BridgeTokenRenewalError() };
};

/**
 * Sign-out teardown: discards in-flight provisioning, in this tab and in any
 * other, so a mint already in the air cannot restore the token after the
 * session has gone, then deletes the user cookies.
 *
 * Call it once the NextAuth session cookie is gone, not before: user work
 * proves the session with the server under the commit lock, and only a bump
 * that follows the cookie's removal is guaranteed to postdate every check the
 * server passed. The delete waits for the lock too, so it lands after any
 * commit in flight — in any tab — instead of between its token and subject
 * writes, which used to leave a dangling `bridgeTokenSubject` behind.
 */
export const resetBridgeTokenProvisioning = (): Promise<void> => {
  sessionProof = undefined;
  const readUserCredentials = () => [
    readBridgeAccessToken(),
    readBridgeRefreshToken(),
    readBridgeTokenSubject(),
  ];
  const signedOutCredentials = readUserCredentials();
  generation += 1;
  // Bumped straight away rather than under the lock: an attempt holding the
  // lock right now reads it on its way out and cleans up after itself.
  bumpBridgeLogoutEpoch();
  inFlight.clear();
  return enqueueCommit(async () => {
    rejectedRenewal = undefined;
    // Another tab may commit a new login before this tab's queue gets the
    // lock. Delete only the credentials observed at sign-out; missing cookies
    // may have expired or been removed by the stale mint's own cleanup.
    if (
      readUserCredentials().every(
        (value, index) =>
          value === undefined || value === signedOutCredentials[index]
      )
    ) {
      deleteUserBridgeTokens();
    }
  });
};
